import {
  mergeClasses,
  Skeleton,
  SkeletonItem,
  Button,
  Spinner,
  Tooltip,
} from "@fluentui/react-components";
import {
  ChevronLeftRegular,
  DataPieRegular,
  ArrowDownloadRegular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { exportProject } from "apps/decooda/decoodaapi";
import { ProjectStatus } from "apps/decooda/components/projects/projectConstants";
import { getStatusDisplayText } from "apps/decooda/components/projects/projectConstants";
import { convertDateStringToUnix, formatDate } from "utils/generalUtils";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { IProjectIQWorkspace } from "models/IProjectIQWorkspace";
import { useMarkAnalyticsWorkspaceUpdated } from "apps/project-iq/hooks/useMarkAnalyticsWorkspaceUpdated";
import { CreateAnalyticsDialog } from "apps/project-iq/components/CreateAnalyticsDialog/CreateAnalyticsDialog";
import { AnalyticsOverviewTab } from "./AnalyticsOverviewTab";
import { AnalyticsRun } from "./AnalyticsRun";
import { AnalyticsResults } from "./AnalyticsResults";
import { useAnalyticsDetailPageStyles } from "./AnalyticsDetailPage.styles";
import { IAnalyticsDetailPageProps } from "./AnalyticsDetailPage.types";
import { useAnalyticsProjectPolling } from "./useAnalyticsProjectPolling";

const ANALYTICS_TAB = {
  OVERVIEW: "Overview",
  RUN: "Run",
  RESULTS: "Results",
} as const;
type AnalyticsTab = (typeof ANALYTICS_TAB)[keyof typeof ANALYTICS_TAB];
const ANALYTICS_TABS = Object.values(ANALYTICS_TAB) as AnalyticsTab[];

const getStatusPillClass = (
  status: ProjectStatus | undefined,
  styles: ReturnType<typeof useAnalyticsDetailPageStyles>,
): string => {
  switch (status) {
    case ProjectStatus.InProgress:
      return mergeClasses(styles.statusPill, styles.statusPillInProgress);
    case ProjectStatus.Scored:
      return mergeClasses(styles.statusPill, styles.statusPillScored);
    case ProjectStatus.Error:
      return mergeClasses(styles.statusPill, styles.statusPillError);
    default:
      return mergeClasses(styles.statusPill, styles.statusPillNotScored);
  }
};

export const AnalyticsDetailPage = ({
  workspaceId,
  analyticsId,
}: IAnalyticsDetailPageProps): JSX.Element => {
  const styles = useAnalyticsDetailPageStyles();
  const navigate = useNavigate();
  const markWorkspaceUpdated = useMarkAnalyticsWorkspaceUpdated(workspaceId);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [activeTab, setActiveTab] = useState<AnalyticsTab>(
    ANALYTICS_TAB.OVERVIEW,
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleTabKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const currentIndex = ANALYTICS_TABS.indexOf(activeTab);
      let nextIndex: number | null = null;
      if (e.key === "ArrowRight")
        nextIndex = (currentIndex + 1) % ANALYTICS_TABS.length;
      else if (e.key === "ArrowLeft")
        nextIndex =
          (currentIndex - 1 + ANALYTICS_TABS.length) % ANALYTICS_TABS.length;
      if (nextIndex !== null) {
        e.preventDefault();
        setActiveTab(ANALYTICS_TABS[nextIndex]);
        tabRefs.current[nextIndex]?.focus();
      }
    },
    [activeTab],
  );

  const workspace = useSelector((state: RootState) =>
    state.projectIqWorkspacesSlice.value.find((w) => w.id === workspaceId),
  ) as IProjectIQWorkspace | undefined;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const {
    project,
    isLoading,
    isError,
    isPendingScore,
    handleProjectUpdated,
    handleScoringInitiated,
  } = useAnalyticsProjectPolling(analyticsId);

  const handleAttachmentsChanged = useCallback(() => {
    void markWorkspaceUpdated(analyticsId);
  }, [markWorkspaceUpdated, analyticsId]);

  const handleExportProject = useCallback(async () => {
    if (!analyticsId) return;
    setIsExporting(true);
    try {
      await exportProject({ projectId: analyticsId });
    } finally {
      setIsExporting(false);
    }
  }, [analyticsId]);

  const handleBack = useCallback(() => {
    navigate(`/workspace/${workspaceId}/ai-analytics`);
  }, [navigate, workspaceId]);

  if (isLoading) {
    return (
      <div
        className={styles.root}
        aria-busy="true"
        aria-label="Loading analytics details"
      >
        <Skeleton>
          <nav aria-hidden="true" className={styles.breadcrumbBar}>
            <SkeletonItem
              shape="rectangle"
              size={32}
              className={styles.skeletonBreadcrumb}
            />
          </nav>
          <div className={styles.contentArea}>
            <div className={mergeClasses(styles.hero, styles.skeletonRow)}>
              <SkeletonItem shape="square" size={40} />
              <div className={styles.skeletonCol}>
                <div className={styles.skeletonRow}>
                  <SkeletonItem
                    shape="rectangle"
                    size={28}
                    className={styles.skeletonHeroName}
                  />
                  <SkeletonItem
                    shape="rectangle"
                    size={20}
                    className={styles.skeletonStatusPill}
                  />
                </div>
                <SkeletonItem
                  shape="rectangle"
                  size={16}
                  className={styles.skeletonHeroSubline}
                />
              </div>
            </div>
            <div className={styles.tabBar} aria-hidden="true">
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabPrimary}
              />
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabSecondary}
              />
              <SkeletonItem
                shape="rectangle"
                size={16}
                className={styles.skeletonTabTertiary}
              />
            </div>
            <SkeletonItem
              shape="rectangle"
              className={styles.skeletonContent}
            />
          </div>
        </Skeleton>
      </div>
    );
  }

  if (isError || !project) {
    return (
      <div className={styles.root}>
        <nav aria-label="Breadcrumb" className={styles.breadcrumbBar}>
          <button
            className={styles.backButton}
            onClick={handleBack}
            aria-label="Back to AI Analytics"
          >
            <ChevronLeftRegular aria-hidden="true" />
            AI Analytics
          </button>
          <span className={styles.breadcrumbSeparator} aria-hidden="true">
            /
          </span>
          <span
            className={styles.breadcrumbName}
            aria-current="page"
            role="status"
          >
            Not found
          </span>
        </nav>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <nav aria-label="Breadcrumb" className={styles.breadcrumbBar}>
        <button
          className={styles.backButton}
          onClick={handleBack}
          aria-label="Back to AI Analytics"
        >
          <ChevronLeftRegular aria-hidden="true" />
          AI Analytics
        </button>
        <span className={styles.breadcrumbSeparator} aria-hidden="true">
          /
        </span>
        <span className={styles.breadcrumbName} aria-current="page">
          {project.name}
        </span>
        <div className={styles.breadcrumbRight}>
          <Tooltip
            content={
              isExporting ? "Export in progress" : "Download Project Data"
            }
            relationship="label"
          >
            <Button
              appearance="outline"
              shape="rounded"
              icon={
                isExporting ? (
                  <Spinner size="tiny" aria-hidden="true" />
                ) : (
                  <ArrowDownloadRegular aria-hidden="true" />
                )
              }
              onClick={handleExportProject}
              disabled={isExporting}
              aria-label="Download project data"
            >
              Project Data
            </Button>
          </Tooltip>
        </div>
      </nav>

      <div className={styles.contentArea}>
        <section className={styles.hero}>
          <div className={styles.heroLeft}>
            <span className={styles.heroIconTile} aria-hidden="true">
              <DataPieRegular fontSize={22} />
            </span>
            <div className={styles.heroBody}>
              <h1
                ref={headingRef}
                tabIndex={-1}
                id="analytics-name"
                className={styles.heroName}
              >
                {project.name}
              </h1>
              <div className={styles.heroSubLine}>
                <span className={styles.heroSubLineAccent}>AI Analytics</span>
                {project.created && (
                  <span>{` · Created ${formatDate(convertDateStringToUnix(project.created))}`}</span>
                )}
              </div>
            </div>
          </div>
          <span
            className={getStatusPillClass(project.status, styles)}
            aria-label={`Status: ${getStatusDisplayText(project.status)}`}
          >
            {getStatusDisplayText(project.status)}
          </span>
        </section>

        <div
          className={styles.tabBar}
          role="tablist"
          aria-label="Analytics sections"
        >
          {ANALYTICS_TABS.map((tab, idx) => {
            const isActive = tab === activeTab;
            return (
              <button
                key={tab}
                ref={(el) => {
                  tabRefs.current[idx] = el;
                }}
                id={`tab-${tab}`}
                role="tab"
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(tab)}
                onKeyDown={handleTabKeyDown}
                className={mergeClasses(
                  styles.tab,
                  isActive && styles.tabActive,
                )}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div
          id={`tabpanel-${activeTab}`}
          role="tabpanel"
          aria-labelledby={`tab-${activeTab}`}
        >
          {activeTab === ANALYTICS_TAB.OVERVIEW && (
            <AnalyticsOverviewTab
              project={project}
              onEdit={() => setIsEditDialogOpen(true)}
              onAttachmentsChanged={handleAttachmentsChanged}
            />
          )}
          {activeTab === ANALYTICS_TAB.RUN && (
            <AnalyticsRun
              project={project}
              isPendingScore={isPendingScore}
              onViewResults={() => setActiveTab(ANALYTICS_TAB.RESULTS)}
              onProjectUpdated={handleProjectUpdated}
              onScoringInitiated={handleScoringInitiated}
            />
          )}
          {activeTab === ANALYTICS_TAB.RESULTS && (
            <AnalyticsResults
              project={project}
              analyticsId={analyticsId}
              workspaceId={workspaceId}
            />
          )}
        </div>

        {isEditDialogOpen && workspace && (
          <CreateAnalyticsDialog
            isOpen={isEditDialogOpen}
            workspaceId={workspaceId}
            workspace={workspace}
            mode="edit"
            project={project}
            onSaved={handleProjectUpdated}
            onDismiss={() => setIsEditDialogOpen(false)}
          />
        )}
      </div>
    </div>
  );
};
